import React, { useState, useEffect } from 'react';
import { motion, Variants } from 'framer-motion';
import { Upload, Edit, Brain, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';

const Home = () => {
    const { signInAnonymously, currentUser } = useAuth();
    const navigate = useNavigate();

    // 🔥 State für Weiterleitung
    const [redirectAfterLogin, setRedirectAfterLogin] = useState<string | null>(null);

    // 🔥 useEffect für automatische Navigation
    useEffect(() => {
        if (currentUser && redirectAfterLogin) {
            navigate(redirectAfterLogin);
            setRedirectAfterLogin(null);
        }
    }, [currentUser, redirectAfterLogin, navigate]);

    const handleGuestStart = async () => {
        if (!currentUser) {
            try {
                setRedirectAfterLogin('/erstellen');
                await signInAnonymously();
            } catch (error) {
                console.error('Anonymer Login fehlgeschlagen', error);
                alert('Start ohne Anmeldung fehlgeschlagen. Bitte versuche es später erneut.');
                setRedirectAfterLogin(null);
            }
        } else {
            navigate('/erstellen');
        }
    };

    const container: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, delayChildren: 0.3 },
        },
    };

    const item: Variants = {
        hidden: { y: 30, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100 } },
    };

    return (
        <div className="bg-gradient-to-b from-sky-50 via-white to-indigo-50 overflow-hidden">
            <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
                <div className="absolute inset-0 -z-10 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
                    <div className="absolute top-40 left-20 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
                        Klausur in{' '}
                        <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                            3 Tagen
                        </span>{' '}
                        und noch nichts gelernt?
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto">
                        Lade dein Skript hoch und erhalte in 60 Sekunden fertige Karteikarten.
                        Kein Zusammenfassen. Kein Stress. Einfach bestehen.
                    </p>
                    <motion.div
                        className="flex flex-col sm:flex-row justify-center gap-4"
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: 'spring', stiffness: 300 }}
                    >
                        <a
                            href="/erstellen"
                            className="group px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-full font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
                        >
                            Skript hochladen & lernen
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </a>
                        <motion.button
                            onClick={handleGuestStart}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-6 py-3 bg-white/80 backdrop-blur-sm text-indigo-600 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all border border-indigo-200"
                        >
                            Kostenlos testen – ohne Anmeldung
                        </motion.button>
                    </motion.div>
                    <p className="text-sm text-gray-500 mt-6 flex items-center justify-center gap-2">
                        <Sparkles className="w-4 h-4 text-indigo-400" />
                        Für gestresste Studenten 2–5 Tage vor der Prüfung
                    </p>
                </motion.div>
            </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{ duration: 0.6 }}
                    className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16"
                >
                    So rettest du deine Prüfung
                </motion.h2>

                <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-100px' }}
                    className="grid grid-cols-1 md:grid-cols-3 gap-8"
                >
                    {[
                        {
                            icon: Upload,
                            title: 'Skript hochladen',
                            desc: 'PDF, Word oder PowerPoint – unsere KI extrahiert die wichtigsten Punkte und erstellt deine Karteikarten in Sekunden.',
                            color: 'from-indigo-500 to-indigo-600',
                        },
                        {
                            icon: Edit,
                            title: 'Karten anpassen',
                            desc: 'Bearbeite oder ergänze Karten, falls du bestimmte Themen priorisieren willst.',
                            color: 'from-purple-500 to-purple-600',
                        },
                        {
                            icon: Brain,
                            title: 'Gezielt lernen',
                            desc: 'Unser Lernmodus fragt nur das ab, was du noch nicht kannst – Zeit sparen und effizient bestehen.',
                            color: 'from-pink-500 to-pink-600',
                        },
                    ].map((feature, index) => (
                        <motion.div
                            key={index}
                            variants={item}
                            whileHover={{ y: -8, transition: { type: 'spring', stiffness: 300 } }}
                            className="group relative bg-white/70 backdrop-blur-lg p-8 rounded-3xl shadow-xl hover:shadow-2xl border border-white/50 transition-all"
                        >
                            <div
                                className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-300`}
                            />
                            <div className="relative z-10">
                                <div
                                    className={`w-14 h-14 mb-6 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                                >
                                    <feature.icon className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold mb-3 text-gray-900">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </section>

            <section className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 py-24 overflow-hidden">
                <div className="absolute inset-0 bg-black opacity-10" />
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 animate-pulse" />

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
                >
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                        Nächste Prüfung in 2–3 Tagen?
                    </h2>
                    <p className="text-indigo-100 mb-10 text-lg md:text-xl">
                        Lade jetzt dein Skript hoch – in 60 Sekunden hast du deine Lernkarten.
                    </p>
                    <motion.a
                        href="/erstellen"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-block px-10 py-4 bg-white text-indigo-600 rounded-full font-semibold shadow-2xl hover:shadow-3xl transition-shadow duration-300"
                    >
                        Skript hochladen & bestehen
                    </motion.a>
                </motion.div>
            </section>

            <style>{`
                @keyframes blob {
                    0%, 100% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                }
                .animate-blob {
                    animation: blob 10s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
            `}</style>
        </div>
    );
};

export default Home;